import React, { useMemo, useState } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import gql from 'graphql-tag'

import { downloadFromUrl } from 'helpers/downloadFromUrl'
import ContentBlock from 'components/ContentBlock'
import { Form, Select, DateTimePicker } from 'components/forms'
import Button from 'components/Button'
import Divider from 'components/Divider'
import { useLazyQuery } from 'components/graphql'
import ProgressBar from 'components/ProgressBar/ProgressBar'


const initTSELogsExport = gql`
query initTSELogsExport($fields: TSELogsExportInput!) {
	cashAssist {
		initTSELogsExport(fields: $fields)
	}
}`

const getTSELogsExport = gql`
query getTSELogsExport($requestId: ID!) {
	cashAssist {
		getTSELogsExport(requestId: $requestId) {
			status
			progress
			error
			data {
				url
			}
		}
	}
}`

const queryCrs = gql`
	query listCashRegisters {
		cashAssist {
			listCashRegisters(activeOnly: true) {
				id
				name
			}
		}
	}
`

const TSELogs = (props) => {
	const [isSubmitting, setSubmitting] = useState(false)
	const [t] = useTranslation()
	const startDay = useMemo(() => moment().utc().startOf('day').toISOString(), [])
	const endDay = useMemo(() => moment().utc().endOf('day').toISOString(), [])

	const handleError = (error) => {
		setSubmitting(false)
	}

	const [getExportData, exportRes] = useLazyQuery(getTSELogsExport, {
		pollInterval: 2000,
		fetchPolicy: 'network-only',
		onError: handleError,
		onCompleted: res => {
			const { status, error, data } = res?.cashAssist?.getTSELogsExport || {}
			if(status === 'READY' || status === 'ERROR') {
				exportRes.stopPolling()
				setSubmitting(false)
			}
			if(data?.url) {
				return downloadFromUrl(data.url)
			}
			if(error) {
				return handleError(error)
			}
		}
	})
	const { status, progress } = exportRes.data?.getTSELogsExport || {}

	const [initExportData] = useLazyQuery(initTSELogsExport, {
		onCompleted: data => {
			getExportData({ variables: { requestId: data.cashAssist?.initTSELogsExport } })
		},
		onError: handleError
	})

	const handleSubmit = ({ period, cashRegisterId }) => {
		setSubmitting(true)
		initExportData({
			variables: {
				fields: {
					period,
					cashRegisterIds: [cashRegisterId]
				}
			}
		})
	}

	return (
		<ContentBlock label={t('options_section.strings.tse_logs')}>
			<Form initialValues={{ period: { from: startDay, to: endDay } }} onSubmit={handleSubmit}>
				<Row>
					<Col xs>
						<Select
							field='cashRegisterId'
							label={t('common.fields.cash_register')}
							remoteOptions={{
								query: queryCrs
							}}
							valueField='id'
							labelField='name'
							isSearchable
							validate={val => val ? null : t('validators.required')}
							validateOnChange
						/>
					</Col>
					<Col xs>
						<DateTimePicker
							field='period'
							label={t('common.fields.date')}
							validate={val => val ? null : t('validators.required')}
							validateOnChange
							interval
						/>
					</Col>
				</Row>
				<Divider />
				<Row middle='xs'>
					<Col xs={2}>
						<Button
							type='submit'
							text={t('common.buttons.apply')}
							loading={isSubmitting}
						/>
					</Col>
					{status === 'LOADING' &&
						<Col xs={1}>
							<ProgressBar progress={progress} width={120}/>
						</Col>
					}
				</Row>
			</Form>
		</ContentBlock>
	)
}

export default TSELogs
